<script setup lang="ts">
import "~/assets/css/kiosk.css";
import "~/assets/css/fonts.css";
import "~/assets/css/main.css";
import "~/assets/css/cb_custom.css";
import "~/assets/css/scrollbar.css";
import buttonBackToUI from "assets/graphics/icons/back_to_ui4.png";
import buttonHome from "assets/graphics/icons/btn_home.svg";
import MainMenuLogo from "~/components/kiosk/MainMenuLogo.vue";
import PinPad from "~/components/kiosk/PinPad.vue";
import { FacilityFirestore } from "~/types/facility";
import { getFontClass, getFontStyle } from "~/util/fontUtils";
import { getThemeAllColorsCodes } from "~/util/themesColors";

useHead({
  meta: [{ name: "google", content: "notranslate" }]
});

const currentRoute = useRoute();
const indexPageRegex = /^\/kiosk\/([\w-]{12}|[\w-]{20})\/?$/;
const isOnIndexPage = computed(() => indexPageRegex.test(currentRoute.path));

const isPreview = computed(() => useRoute().query.preview || false);
const goBackPreview = useGoBackPreviewDebounced();
const goHome = useGoHomeDebounced();

const facilityStore = useFacilityStore();
const facilityStoreRefs = storeToRefs(facilityStore);
const facility: Ref<FacilityFirestore | null> = facilityStoreRefs.facility;

// Define a regex pattern to match the required routes
const navigationBackRoutesRegex =
    /^\/kiosk\/([\w-]{12}|[\w-]{20})\/(accommodation|contactteam|imagegallery|news)\/[\w-]+\/?$/i;

// Computed property to check if the navigation back button should be shown
const showNavigationBackButton = computed(() => navigationBackRoutesRegex.test(currentRoute.path));

// disable right click
document.addEventListener("contextmenu", (event) => event.preventDefault());

// Settings
const pinInputVisible = ref(false);
const settingsPIN = "4321"; // TODO: make configurable
const openPinPad = () => {
  if (isOnIndexPage.value) {
    pinInputVisible.value = true;
  }
};

const confirmPin = () => {
  closePinPad();
  const settingsUrl = `http://careday-settings?returnUrl=${window.location.href}`;
  navigateTo(settingsUrl, { external: true });
};

const closePinPad = () => {
  // workaround to prevent accidental click on menu card
  setTimeout(() => {
    pinInputVisible.value = false;
  }, 100);
};

const timeString = useTimeString();
const dateString = useDateString();
const bgColor = ref<string>("");

watchEffect(() => {
  const themeColors = getThemeAllColorsCodes(
    facility?.value?.designThemeConfig?.colorTheme || "theme-light-green"
  );
  bgColor.value = themeColors?.primaryDark || "";
});

const font = computed(() => facility?.value?.designThemeConfig?.font || "Cardium A");

const {
  handleClick: pressButton,
  isPressedDown: isButtonPressed,
  setPressed: setButtonPressed,
  unsetPressed: unsetButtonPressed
} = useButton(goHome);
</script>

<template>
  <div
    class="kiosk"
    :class="getFontClass(font)"
    :style="[{ backgroundColor: bgColor || '' }, getFontStyle(font)]"
  >
    <div class="kiosk-container">
      <div id="navi">
        <img
          v-show="isPreview"
          class="btn_back"
          :src="buttonBackToUI"
          alt="nav"
          @click="goBackPreview"
        >
        <KioskNavigationBack
          v-if="showNavigationBackButton"
          class="btn-kiosk-navigation-back"
        />

        <img
          v-show="!isOnIndexPage && !isPreview"
          id="btn_home"
          :src="buttonHome"
          alt="nav"
          :class="{ 'pressed-kiosk-card': isButtonPressed }"
          @mousedown="setButtonPressed"
          @touchstart.prevent="setButtonPressed"
          @mouseup="unsetButtonPressed"
          @mouseleave="unsetButtonPressed"
          @touchend="pressButton"
          @click="pressButton"
        >
      </div>

      <div class="head">
        <slot name="head" />
      </div>

      <PinPad
        v-if="pinInputVisible"
        :pin="settingsPIN"
        @confirm-pin="confirmPin"
        @close-pin-pad="closePinPad"
      />

      <slot name="cards" />
      <div class="footer">
        <slot name="footer" />
      </div>
      <div class="info">
        <div class="info-header">
          <MainMenuLogo @logo-click="openPinPad" />
          <span class="info-date cb_remove_on_mobile">
            {{ dateString }}
          </span>
          <span class="info-time cb_remove_on_mobile">
            {{ timeString }}
          </span>
        </div>

        <div class="info-content cb_remove_on_mobile">
          <slot name="info" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
body,
html {
  margin: 0 !important;
}

.footer:empty {
  display: none;
}

.kiosk {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  background-image: url("@/assets/graphics/img/kiosk/Careday_bg_white_elements.svg");
  background-repeat: no-repeat;
  background-size: cover;
  touch-action: none;
  user-select: none;
}

.display_none {
  display: none;
}

#navi {
  grid-area: navi;
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Info Section */
.info {
  grid-area: info;
  height: 100vh;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 20% 80%;
  grid-template-areas:
    "header"
    "content";
}

.info-header {
  grid-area: header;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  padding-top: 2rem;
}

.info-header .info-logo {
  width: 75%;
}

.info-datetime {
  width: 75%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  justify-content: space-between;
}

.info-date,
.info-time {
  width: 75%;
  font-size: 2vw;
  color: var(--ct_E1);
  font-weight: 400;
  text-align: right;
  justify-content: flex-end;
}

.info-content {
  grid-area: content;
  padding-top: 2rem;
  margin-top: 3rem;
}

.btn_back {
  position: absolute;
  top: 3.75rem;
  width: 100%;
  padding-right: 1rem;
}

#btn_home {
  position: absolute;
  bottom: 3rem;
  width: 80%;
  background-image: url("@/assets/graphics/icons/cd-home-btn.svg");
  background-repeat: no-repeat;
  background-size: cover;
}

.pressed-kiosk-card {
  transform: scale(0.98);
}

.head {
  grid-area: head;
}

@media (min-width: 1366px) {
  .info {
    grid-template-rows: 20% 80%;
    padding-right: 3rem;
  }
}

@media (min-width: 1920px) {
  .info {
    padding-right: 2rem;
  }

  .info-header {
    padding-top: 2rem;
  }

  .info-content {
    padding: 3rem 0 0 2rem;

  }
}

@media (min-width: 3200px) {
  .info {
    padding-right: 0;
  }

  .info-header {
    padding-top: 5rem;
  }

  .info-content {
    padding: 6rem 0 0 2.5rem;
  }
}
</style>
